/**
 * The user-initiated update session — the one source of truth behind the
 * "Updating Syntry…" wall (`UpdateProgressHost.vue`), and the handoff that lets
 * the reloaded build confirm the jump with an "Update complete" toast.
 *
 * A LEAF in the same layer as `register.ts`, which drives it: it imports only
 * vue, `@/utils/app-info` and `./reload`, so it is safe to evaluate before the
 * router, the stores and the realtime service exist. A module-level singleton in
 * the shape of `src/composables/useAuthProgress.ts` (readonly refs, one timer).
 * Sessions never run in the native shell — neither update prompt can fire there
 * — so, unlike useAuthProgress, there is no `appState` wake.
 *
 * ── Phases ──────────────────────────────────────────────────────────────────
 *
 *   idle ──tap──► updating ──reload sent──► restarting ──► (the document dies)
 *                     └──────────────┬─────────────┘
 *                 STALLED_AFTER_MS after the last step, page still alive
 *                                    ▼
 *                    stalled — the wall stays up and offers Reload
 *
 * There is deliberately no "failed → give the app back" state. Once anything
 * has happened — SKIP_WAITING sent (a newer worker may now control this page's
 * old code), the worker unregistered, the precache purged — the page underneath
 * may no longer work, so handing it back would be worse than waiting. The only
 * way back to `idle` with the page alive is {@link abortUpdateSession}, and it
 * is used only BEFORE anything destructive happened.
 *
 * ── The dwell ───────────────────────────────────────────────────────────────
 * Nothing is switched or deleted until the wall has been up MIN_VISIBLE_MS
 * ({@link whenOverlayShown}), so it never just flashes on a fast phone — and
 * because SKIP_WAITING waits for it, the old page never runs under the new
 * worker while the user is looking at the wall.
 *
 * ── Deadlines are timestamps, not delays ────────────────────────────────────
 * Same reasoning as useAuthProgress.ts: a hidden tab clamps `setTimeout` and a
 * frozen one suspends it, so every wake re-reads the clock. One extra rule: a
 * wake — or a timer that fires far later than it was armed for, which is what a
 * resume from a frozen page looks like — holds the step fallbacks and the stall
 * for WAKE_GRACE_MS, so a `controllerchange` that queued up while the page was
 * frozen is delivered before an overdue fallback decides the update is stuck.
 *
 * ── The handoff ─────────────────────────────────────────────────────────────
 * `sessionStorage[HANDOFF_KEY]`: per tab (only the tab the user watched gets
 * the "you asked for this" toast), it survives `location.reload()` and
 * `hardReload()` (which clears Cache Storage only), and it dies with the tab.
 *
 * The reloaded page compares the record with itself, and "the app changed" is
 * ANY of three facts — the first alone used to decide it:
 *   - a different `version`/`build` pair (`package.json`, via `define`);
 *   - a different ENTRY MODULE — the `src` of index.html's one
 *     `<script type="module">`, content-hashed, into which the bundler folds
 *     the hashes of everything it imports. A rebuild deployed on the same pair
 *     (a republish without a bump; every `build:verify` cycle) is invisible to
 *     the pair and visible here. `null` on either side (dev, tests) means
 *     "unknown" and decides nothing;
 *   - `landed`: the reload was sent because an update worker took control of
 *     an already-controlled page (`register.ts`), so the page it produced comes
 *     from that worker's precache — the new deploy — whatever the numbers say.
 * Before the last two, a same-pair republish came back as "couldn't finish
 * updating" on an app that had.
 *
 * Builds before 0.16 wrote a plain version string to
 * `localStorage[LEGACY_HANDOFF_KEY]` instead — and the FIRST update into this
 * build is run by that old code — so the reader keeps accepting it. Records
 * from 0.16–0.17.2 carry neither `entry` nor `landed`; the parser reads them as
 * unknown / false, which is exactly the old verdict.
 */
import { computed, readonly, ref, type ComputedRef, type Ref } from 'vue';
import { APP_BUILD, APP_VERSION } from '@/utils/app-info';
import { reloadPage } from './reload';

/** Which build — the pair `version.json` publishes and `sw.ts` answers with. */
export interface BuildId {
  version: string;
  /** Numeric string in practice; `null` when the source did not say. */
  build: string | null;
}

export type UpdatePhase =
  /** No update running. The wall is down. */
  | 'idle'
  /** The user tapped Update: the wall is up while the new build is fetched and switched in. */
  | 'updating'
  /** The reload has been sent; the document is on its way out. */
  | 'restarting'
  /** Still alive long after the last step — the wall stays, with a Reload button. */
  | 'stalled';

/** The wall is on screen at least this long before anything is switched or deleted. */
export const MIN_VISIBLE_MS = 1_500;

/** When the wall admits the wait is taking longer than usual. */
export const SLOW_AFTER_MS = 8_000;

/** How long after the last step (the tap, SKIP_WAITING, the reload) before `stalled`. */
export const STALLED_AFTER_MS = 20_000;

/** After a wake, how long step fallbacks and the stall hold off. */
export const WAKE_GRACE_MS = 1_500;

/** A timer firing this much later than armed for means the page was frozen. */
const LATE_TIMER_MS = 1_000;

/** A handoff older than this is ignored at boot (sessionStorage outlives a browser restart). */
export const HANDOFF_TTL_MS = 30 * 60_000;

/** sessionStorage key: written when the update starts, read by the reloaded build. */
export const HANDOFF_KEY = 'syntry.pwa_update_handoff';

/** localStorage key the pre-0.16 builds wrote (a plain version string). */
export const LEGACY_HANDOFF_KEY = 'syntry.pwa_updated_from';

/** Never schedule a zero/negative timeout — a late wake must re-check, not spin. */
const MIN_TIMER_MS = 16;

const phase = ref<UpdatePhase>('idle');
const slow = ref(false);
const target = ref<BuildId | null>(null);

/** The tap. The dwell and the slow line are measured from here. */
let startedAt = 0;
/** The last step. The stall is measured from here. */
let stepAt = 0;
/** The current step's own fallback, armed by {@link armUpdateStall}. */
let step: { at: number; run: () => void } | null = null;
/** Step fallbacks and the stall wait until this timestamp (a wake just happened). */
let graceUntil = 0;
let timer: ReturnType<typeof setTimeout> | undefined;
/** When the pending timer was meant to fire — a much later tick is a resume. */
let armedFor = 0;
let dwellWaiters: Array<() => void> = [];
let detachWake: (() => void) | null = null;

/** The current update phase. */
export const updatePhase: Readonly<Ref<UpdatePhase>> = readonly(phase) as Readonly<Ref<UpdatePhase>>;

/** The wait has become longer than usual (only meaningful while `updating`). */
export const updateSlow: Readonly<Ref<boolean>> = readonly(slow) as Readonly<Ref<boolean>>;

/** The build being moved to, once known — for the wall's version line. */
export const updateTarget = readonly(target) as Readonly<Ref<Readonly<BuildId> | null>>;

/** The wall should be up: any phase but `idle`, `stalled` included. */
export const updateInProgress: ComputedRef<boolean> = computed(() => phase.value !== 'idle');

function isActive(): boolean {
  return phase.value !== 'idle';
}

function isClockRunning(): boolean {
  return phase.value === 'updating' || phase.value === 'restarting';
}

function stopTimer(): void {
  if (timer !== undefined) clearTimeout(timer);
  timer = undefined;
}

function flushDwell(): void {
  const waiters = dwellWaiters;
  dwellWaiters = [];
  for (const resolve of waiters) resolve();
}

/** The earliest deadline anything is waiting for, or +∞. */
function nextDeadline(): number {
  const deadlines: number[] = [];
  if (dwellWaiters.length > 0) deadlines.push(startedAt + MIN_VISIBLE_MS);
  if (phase.value === 'updating' && !slow.value) deadlines.push(startedAt + SLOW_AFTER_MS);
  if (step) deadlines.push(Math.max(step.at, graceUntil));
  if (isClockRunning()) deadlines.push(Math.max(stepAt + STALLED_AFTER_MS, graceUntil));
  return deadlines.length > 0 ? Math.min(...deadlines) : Number.POSITIVE_INFINITY;
}

/** Arm the one timer for the nearest deadline, measured from the wall clock. */
function arm(): void {
  stopTimer();
  if (!isActive()) return;
  const deadline = nextDeadline();
  if (deadline === Number.POSITIVE_INFINITY) return;
  const now = Date.now();
  armedFor = Math.max(deadline, now + MIN_TIMER_MS);
  timer = setTimeout(tick, armedFor - now);
}

/**
 * Evaluate every deadline against the real clock. Called by the timer AND by
 * every wake — a throttled or suspended timer fires LATE, never early.
 */
function tick(): void {
  // Also reached from a wake with a timer still pending — never leave a stray one.
  stopTimer();
  if (!isActive()) return;
  const now = Date.now();
  if (now - armedFor > LATE_TIMER_MS) graceUntil = Math.max(graceUntil, now + WAKE_GRACE_MS);

  if (now >= startedAt + MIN_VISIBLE_MS) flushDwell();
  if (phase.value === 'updating' && !slow.value && now >= startedAt + SLOW_AFTER_MS) {
    slow.value = true;
  }

  if (now >= graceUntil) {
    if (step && now >= step.at) {
      const { run } = step;
      step = null;
      // May reload, mark restarting (which resets the stall clock), or arm the next step.
      try {
        run();
      } catch (error) {
        console.warn('[pwa] update fallback failed', error);
      }
      if (!isActive()) return;
    }
    if (isClockRunning() && now >= stepAt + STALLED_AFTER_MS) phase.value = 'stalled';
  }
  arm();
}

function onWake(): void {
  if (!isActive()) return;
  graceUntil = Date.now() + WAKE_GRACE_MS;
  tick();
}

function onVisibilityChange(): void {
  if (document.visibilityState === 'visible') onWake();
}

/**
 * A page restored from the back/forward cache mid-update carries state from
 * before it was frozen — including, possibly, a reload that was already sent.
 * Reload straight away rather than trust any of it.
 */
function onPageShow(event: PageTransitionEvent): void {
  if (!isActive()) return;
  if (event.persisted) {
    reloadPage();
    return;
  }
  onWake();
}

function attachWake(): void {
  if (detachWake || typeof window === 'undefined') return;
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('focus', onWake);
  window.addEventListener('pageshow', onPageShow);
  detachWake = (): void => {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('focus', onWake);
    window.removeEventListener('pageshow', onPageShow);
  };
}

/* ── The session ──────────────────────────────────────────────────────────── */

/** True from the tap until the document dies (or the session is aborted). */
export function isUpdateSessionActive(): boolean {
  return isActive();
}

/**
 * Put the wall up and start the clocks. Idempotent: a second call — a double
 * tap, or the watchdog handing over to `applyUpdate()` — keeps the original
 * start time and only fills in a target that was still unknown.
 */
export function beginUpdateSession(options: { target?: BuildId | null } = {}): void {
  if (isActive()) {
    if (!target.value && options.target) target.value = { ...options.target };
    return;
  }
  const now = Date.now();
  startedAt = now;
  stepAt = now;
  step = null;
  graceUntil = 0;
  slow.value = false;
  target.value = options.target ? { ...options.target } : null;
  phase.value = 'updating';
  stashUpdateHandoff({ explicit: true });
  attachWake();
  arm();
}

/** The build being moved to, once a probe knows it better than the caller did. */
export function setUpdateTarget(next: BuildId): void {
  if (!isActive()) return;
  target.value = { ...next };
}

/**
 * Resolves once the wall has been on screen MIN_VISIBLE_MS — immediately when
 * no session is running or the dwell is already over. Callers re-check their
 * own state after it: the session may have moved on while they waited.
 */
export function whenOverlayShown(): Promise<void> {
  if (!isActive() || Date.now() >= startedAt + MIN_VISIBLE_MS) return Promise.resolve();
  return new Promise<void>((resolve) => {
    dwellWaiters.push(resolve);
    arm();
  });
}

/**
 * Mark a new step (it restarts the stall clock) and give it its own fallback:
 * `run` fires `afterMs` from now unless the step is superseded first — by
 * {@link markRestarting}, another `armUpdateStall`, or the document going away.
 */
export function armUpdateStall(afterMs: number, run: () => void): void {
  if (!isActive()) return;
  stepAt = Date.now();
  step = { at: stepAt + afterMs, run };
  arm();
}

/** A reload has been sent: "Restarting Syntry…", and a fresh stall clock for it. */
export function markRestarting(): void {
  if (!isActive()) return;
  phase.value = 'restarting';
  step = null;
  stepAt = Date.now();
  arm();
}

/**
 * Give the app back — ONLY before anything destructive happened (the watchdog
 * found the device offline, or the host unreachable, before touching the
 * worker or the caches). Clears the handoff, stops every clock, and releases
 * anyone waiting on the dwell.
 */
export function abortUpdateSession(): void {
  const wasActive = isActive();
  stopTimer();
  step = null;
  graceUntil = 0;
  phase.value = 'idle';
  slow.value = false;
  target.value = null;
  detachWake?.();
  detachWake = null;
  if (wasActive) removeHandoff();
  flushDwell();
}

/* ── The handoff ──────────────────────────────────────────────────────────── */

interface HandoffRecord {
  v: 1;
  /** The build that was running when the reload was sent. */
  from: BuildId;
  /** Epoch ms of the stash. */
  at: number;
  /** The user asked for this update (true), or this tab was reloaded because another one did. */
  explicit: boolean;
  /** The entry module's `src` when the reload was sent; `null` when the page had none. */
  entry: string | null;
  /** The reload was sent because an update worker took control (see the file docblock). */
  landed: boolean;
}

export type UpdateOutcome =
  /**
   * The app changed under the reload: a different version/build pair, a
   * different entry module, or a new worker took control (the docblock).
   */
  | { kind: 'updated'; from: BuildId; explicit: boolean }
  /** The reload came back on the same build with no sign the app changed — the update did not land. */
  | { kind: 'unchanged'; explicit: boolean };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * The `src` of this document's entry module — `/assets/index-<hash>.js` in a
 * build, `/src/main.ts` in dev, `null` where there is no such tag (tests).
 */
function currentEntryUrl(): string | null {
  if (typeof document === 'undefined') return null;
  try {
    const src = document.querySelector('script[type="module"][src]')?.getAttribute('src') ?? '';
    return src !== '' ? src : null;
  } catch {
    return null;
  }
}

function parseRecord(raw: string | null): HandoffRecord | null {
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (!isRecord(value) || value.v !== 1 || !isRecord(value.from)) return null;
    const { version, build } = value.from;
    if (typeof version !== 'string' || version === '') return null;
    if (build !== null && typeof build !== 'string') return null;
    if (typeof value.at !== 'number' || !Number.isFinite(value.at)) return null;
    if (typeof value.explicit !== 'boolean') return null;
    // Both newer fields are optional on the wire: a record an older build wrote
    // (or a mangled one) reads as "entry unknown, not landed" — the old verdict.
    const entry = typeof value.entry === 'string' && value.entry !== '' ? value.entry : null;
    return {
      v: 1,
      from: { version, build },
      at: value.at,
      explicit: value.explicit,
      entry,
      landed: value.landed === true,
    };
  } catch {
    return null;
  }
}

function peekSession(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Read and remove, in one guarded step. */
function take(area: 'session' | 'local', key: string): string | null {
  try {
    const storage = area === 'session' ? window.sessionStorage : window.localStorage;
    const value = storage.getItem(key);
    if (value !== null) storage.removeItem(key);
    return value;
  } catch {
    return null;
  }
}

function removeHandoff(): void {
  try {
    window.sessionStorage.removeItem(HANDOFF_KEY);
  } catch {
    /* no-op */
  }
}

/**
 * Record the running build so the page that loads next can tell whether it is a
 * different one. A passive stash (a tab reloaded because ANOTHER tab applied the
 * update) never overwrites a fresh explicit one — but `landed` is a fact about
 * THIS reload, so a landed passive stash adds it to the explicit record rather
 * than losing it.
 */
export function stashUpdateHandoff(options: { explicit: boolean; landed?: boolean }): void {
  const now = Date.now();
  const landed = options.landed === true;
  let record: HandoffRecord | null = null;
  if (!options.explicit) {
    const existing = parseRecord(peekSession(HANDOFF_KEY));
    if (existing?.explicit && now - existing.at < HANDOFF_TTL_MS) {
      if (!landed || existing.landed) return;
      record = { ...existing, landed: true };
    }
  }
  record ??= {
    v: 1,
    from: { version: APP_VERSION, build: APP_BUILD },
    at: now,
    explicit: options.explicit,
    entry: currentEntryUrl(),
    landed,
  };
  try {
    window.sessionStorage.setItem(HANDOFF_KEY, JSON.stringify(record));
  } catch {
    /* private mode / quota — the reloaded page simply shows no toast */
  }
}

/**
 * The verdict (the file docblock): the version/build pair, the entry module —
 * only when BOTH sides know theirs — or `landed`, the service-worker path's own
 * proof. Any one of them changed means the app did.
 */
function compareWithRunning(record: Pick<HandoffRecord, 'from' | 'explicit' | 'entry' | 'landed'>): UpdateOutcome {
  const { from, explicit } = record;
  const pairChanged = from.version !== APP_VERSION || (from.build !== null && from.build !== APP_BUILD);
  const running = currentEntryUrl();
  const entryChanged = record.entry !== null && running !== null && record.entry !== running;
  const changed = pairChanged || entryChanged || record.landed;
  return changed ? { kind: 'updated', from, explicit } : { kind: 'unchanged', explicit };
}

/**
 * Read and clear the handoff the previous page left — the new format first,
 * then the legacy key (always cleared, so it can never fire twice).
 *
 * @returns null when there is nothing to report: no record, a malformed one, or
 *   one older than HANDOFF_TTL_MS.
 */
export function consumeUpdateHandoff(now: number = Date.now()): UpdateOutcome | null {
  const record = parseRecord(take('session', HANDOFF_KEY));
  const legacy = take('local', LEGACY_HANDOFF_KEY)?.trim() ?? '';
  if (record) {
    if (now - record.at > HANDOFF_TTL_MS) return null;
    return compareWithRunning(record);
  }
  if (legacy !== '') {
    return compareWithRunning({ from: { version: legacy, build: null }, explicit: true, entry: null, landed: false });
  }
  return null;
}
